import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import dataServices from '@/api/dataServices';
import { AuthUserSettings, PublicSettings } from '@/interfaces/publicSettings';

interface PublicSettingsState {
  settings: PublicSettings;
  authUserSettings: AuthUserSettings | null;
  loading: boolean;
  error: string | null;
}

const initialState: PublicSettingsState = {
  settings: {
    contactInfo: {},
    bankInfo: {},
    allowedCurrencies: [],
    supportedLanguages: [],
    branding: {
      logoUrl: null,
      bannerUrl: null,
    },
  },
  authUserSettings: null,
  loading: false,
  error: null,
};

export const fetchPublicSettings = createAsyncThunk('publicSettings/fetch', async (_, { rejectWithValue }) => {
  try {
    const result = await dataServices.getPublicSettingsData();
    return result;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || 'Failed to fetch public settings');
  }
});

export const fetchPublicAuthUserSettings = createAsyncThunk('publicSettings/fetchAuthUser', async (_, { rejectWithValue }) => {
  try {
    const result = await dataServices.getPublicAuthUserSettingsData();
    return result;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || 'Failed to fetch public auth user settings');
  }
});

export const publicSettingsSlice = createSlice({
  name: 'publicSettings',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchPublicSettings.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPublicSettings.fulfilled, (state, action) => {
      state.settings = action.payload;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(fetchPublicSettings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch public settings';
    });

    builder.addCase(fetchPublicAuthUserSettings.pending, state => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchPublicAuthUserSettings.fulfilled, (state, action) => {
      state.authUserSettings = action.payload;
      state.loading = false;
      state.error = null;
    });
    builder.addCase(fetchPublicAuthUserSettings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || 'Failed to fetch public auth user settings';
    });
  },
});

export const selectPublicSettings = (state: RootState) => state.publicSettings;

export default publicSettingsSlice.reducer;
